import { useCallback, useEffect, useState } from 'react'
import FormMessageBanner from '../../../../components/common/FormMessageBanner'
import { ReactComponent as AlertIcon } from '../../../../assets/icons/alert-circle.svg'
import { Controller, useForm } from 'react-hook-form';
import { Input } from '../../../../components/common/Input';
import InputAndSelect from '../../../../components/common/InputAndSelect';
import { stableCoinsList } from '../../const';
import { useParams } from 'react-router-dom';
import { ContentWrapper, Error, RateBox } from './styles';
import { Button } from '../../../../components/common/Button';
import Loader from '../../../../components/common/Loader';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { formatCurrencyAmount } from '../../../../utils/currency';
import { savedPayoutV2Data } from '../../redux/formDataSlice';
import { useStableCoinFee, useStableCoinNetworks } from '../../../../app/queries/stableCoins';
import CustomSelect from '../../../../components/common/CustomSelect';
import { usePopulateFormFromSavedData } from '../hooks';

type CoinTransferDetailsType = {
    network?: string;
    walletAddress?: string;
    paymentDescription?: string;
    amount?: number;
    totalAmount?: number;
    handleNext?: () => void;
    processingFee?: number;
    currency?: string;
}

const Amount = ({ handleNext }: CoinTransferDetailsType) => {

    const { currencyCode } = useParams<{ currencyCode: string }>();
    const capitalizedCurrencyCode = currencyCode?.toUpperCase();

    const { handleSubmit, control, setValue, watch, formState: { errors, isValid } } = useForm<CoinTransferDetailsType>()
    const amountWatch = watch("amount")
    const networkWatch = watch("network")

    const dispatch = useAppDispatch()
    const { loading } = useAppSelector((state) => state?.wallets || {});
    const { businessId } = useAppSelector((state) => state?.users)
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);
    usePopulateFormFromSavedData(getSavedData, setValue);

    const stableCoinOption = stableCoinsList?.filter((item) => item.value === capitalizedCurrencyCode)

    const [debouncedAmount, setDebouncedAmount] = useState<number>()

    const { data: stableCoinNetworksList, isLoading: isNetworkFetchLoading } = useStableCoinNetworks({ currency: capitalizedCurrencyCode, businessId: businessId });
    const { data: feeData, isLoading: isFeeLoading } = useStableCoinFee(businessId, { currency: capitalizedCurrencyCode, amount: debouncedAmount, flow: "send", network: networkWatch })

    useEffect(() => {
        if (feeData) {
            setValue("totalAmount", Number(feeData?.totalDebit))
            setValue("processingFee", Number(feeData?.fee))
        }
    }, [feeData])

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAmount(amountWatch)
        }, 500)
        return () => clearTimeout(timer)
    }, [amountWatch])

    const handleNextStep = useCallback((data: CoinTransferDetailsType) => {
        dispatch(savedPayoutV2Data({
            ...data,
            currency: capitalizedCurrencyCode,
        }))
        handleNext?.();
    }, [dispatch, handleNext]);

    return (
        <>
            <Loader isLoading={loading || isNetworkFetchLoading || isFeeLoading} />

            <form onSubmit={handleSubmit(handleNextStep)}>
                <ContentWrapper>
                    <FormMessageBanner
                        title="Verify your address and network"
                        body="Klasha is not liable for incorrect address or network errors. Always double-check before making payment."
                        icon={<AlertIcon />}
                    />

                    <div>
                        <Controller
                            name="network"
                            control={control}
                            rules={{ required: "Network is required" }}
                            render={({ field }) => {
                                return (
                                    <CustomSelect
                                        placeholder="Network"
                                        $width="100%"
                                        $height="48px"
                                        field={field}
                                        // disableLabel
                                        options={stableCoinNetworksList}
                                        // onChange={handleBankChange}
                                        onChange={field.onChange}
                                        value={field.value}
                                        showSearch
                                        filterOption={(inputValue, option) =>
                                            option.label.toLowerCase().includes(inputValue.toLowerCase())
                                        }
                                        lightBorder
                                    />
                                )
                            }}
                        />
                        {<Error>{errors.network?.message}</Error>}
                    </div>
                    <div>
                        <Controller
                            name="walletAddress"
                            control={control}
                            rules={{ required: "Wallet address is required" }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        {...field}
                                        label="Recipient Wallet Address"
                                        height='48px'
                                    />
                                )
                            }}
                        />
                        {<Error>{errors.walletAddress?.message}</Error>}
                    </div>
                    <div>
                        <Controller
                            name="amount"
                            control={control}
                            rules={{ required: "Amount is required" }}
                            render={({ field }) => {
                                return (
                                    <InputAndSelect
                                        amountValue={field.value as number}
                                        onAmountChange={(value) => {
                                            field.onChange(value);
                                        }}
                                        width="100%"
                                        placeholder="Amount"
                                        options={stableCoinOption}
                                        selectValue={capitalizedCurrencyCode ?? ""}
                                    />
                                )
                            }}
                        />
                        {<Error>{errors?.amount?.message}</Error>}
                    </div>
                    {!!feeData &&
                        <div>
                            <RateBox>
                                <span className="fee-box">
                                    +
                                    <span>
                                        {formatCurrencyAmount(capitalizedCurrencyCode, Number(feeData?.fee), 2)}
                                    </span>
                                </span>
                                <span>Processing fees</span>
                            </RateBox>
                        </div>
                    }
                    {amountWatch &&
                        <div>
                            <Controller
                                name="totalAmount"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <InputAndSelect
                                            amountValue={field.value as number}
                                            width="100%"
                                            placeholder="Total Amount"
                                            options={stableCoinOption}
                                            readOnly
                                            selectValue={capitalizedCurrencyCode ?? ""}
                                        />
                                    )
                                }}
                            />
                        </div>
                    }
                    <div>
                        <Controller
                            name="paymentDescription"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <Input
                                        {...field}
                                        label="Enter payment description"
                                        height='48px'
                                    />
                                )
                            }}
                        />
                        {<Error>{errors.paymentDescription?.message}</Error>}
                    </div>
                    <Button
                        height="48px"
                        label="Continue"
                        disabled={loading || !isValid}
                        onClick={handleSubmit(handleNextStep)}
                    />
                </ContentWrapper>

            </form>
        </>

    )
}

export default Amount